/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Alert } from 'antd'
import React, { useCallback, useMemo, useState } from 'react'
import { TextInput } from 'src/components/ts-components/TextInput'
import Text from 'src/components/ts-components/Text'
import ModernPhoneInput from 'src/components/ts-components/ModernPhoneInput'
import CopyComponent from 'src/components/CopyComponent'
import useWindowSize from 'src/hooks/useWindowSize'
import { useUserContext } from './UserContext'
import RoleSelect from '../OfficialModal/RoleSelect'
import UserDeleteModal from './UserDeleteModal'
import { ROLE_TYPE_LIST } from '@sportninja/common/constants/app'
import { getEntityLabel } from 'src/utils/getEntityLabel'

const isUserOwner = (roleTypeId: number | null | undefined) => {
  return roleTypeId === 0
}

// IDs 3 (Scorekeeper) and 4 (Referee) are official-only roles, not user roles
const roleFullNames: Record<number, string> = {
  0: 'Owner',
  1: 'Admin',
  2: 'League Manager',
  5: 'Videographer',
  6: 'Team Manager',
}

const filterUserRoles = (roleTypeId: number | null | undefined) => {
  const roleList = ROLE_TYPE_LIST as unknown as Array<{
    id: number
    name: string
    name_full: string
  }>
  if (isUserOwner(roleTypeId)) {
    return roleList.filter((type) => type.id === 0)
  }
  return roleList
    .filter((type) => type.id !== 0 && type.id !== 7)
    .map((type) => ({
      ...type,
      name_full: roleFullNames[type.id] || type.name,
    }))
}

const fieldWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

interface DeleteButtonProps {
  onClick?: () => void
}

/**
 * Renders the destructive remove-user action button.
 * @param props - Component props.
 * @param props.onClick - Optional click handler for the delete button.
 */
const DeleteButton: React.FC<DeleteButtonProps> = ({ onClick }) => {
  return (
    <button
      type='button'
      onClick={onClick}
      css={css`
        display: flex;
        padding: 8px 16px 8px 8px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        border-radius: 8px;
        border: 1px solid ${colors.ERROR_200};
        background: ${colors.ERROR_200};
        box-shadow: 0px 2px 0px 0px rgba(5, 145, 255, 0.1);
        cursor: pointer;
        &:hover {
          opacity: 0.9;
        }
      `}
    >
      <DeleteOutlined
        css={css`
          font-size: 24px;
          color: ${colors.NEUTRAL_0};
        `}
        width={24}
        height={24}
      />
      <Text variant='heading6' weight='semiBold' color={colors.NEUTRAL_0}>
        REMOVE USER
      </Text>
    </button>
  )
}

const UserModalContent: React.FC = () => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const { state, dispatch, isEdit, onDelete, entityType } = useUserContext()

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const entityLabel = useMemo(() => getEntityLabel(entityType), [entityType])

  const handleFieldChange = useCallback(
    (field: string) => (value: string) => {
      dispatch({
        type: 'SET_FIELD',
        field: field as keyof typeof state,
        value,
      })
    },
    [dispatch]
  )

  const handleRoleChange = useCallback(
    (value: number) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'role_type_id',
        value,
      })
    },
    [dispatch]
  )

  const handlePhoneChange = useCallback(
    (value: string | undefined) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'phone_number',
        value: value || '',
      })
    },
    [dispatch]
  )

  const handlePhoneCountryChange = useCallback(
    (country: string | undefined) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'phone_number_country',
        value: country || 'US',
      })
    },
    [dispatch]
  )

  const handleDeleteClick = useCallback(() => {
    setDeleteError(null)
    setIsDeleteModalOpen(true)
  }, [])

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false)
    setDeleteError(null)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    try {
      setIsDeleting(true)
      setDeleteError(null)
      await onDelete()
      setIsDeleteModalOpen(false)
    } catch (e: unknown) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to remove user')
    } finally {
      setIsDeleting(false)
    }
  }, [onDelete])

  const hasExistingEmail = isEdit && state.hasInitialEmail

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 16px;
        height: 100%;
        overflow-y: auto;
        width: 100%;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          Admin Info
        </Text>
        {isEdit && state.id ? (
          <div>
            <CopyComponent id={state.id} />
          </div>
        ) : null}
      </div>

      <div
        css={css`
          display: flex;
          gap: 16px;
          ${isMobile &&
          css`
            flex-direction: column;
          `}
        `}
      >
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <TextInput
            name='name_first'
            label='First Name'
            value={state.name_first}
            setValue={handleFieldChange('name_first')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            placeholder='First Name'
            required
          />
        </div>
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <TextInput
            name='name_last'
            label='Last Name'
            value={state.name_last}
            setValue={handleFieldChange('name_last')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            placeholder='Last Name'
            required
          />
        </div>
      </div>

      <div css={fieldWrapperStyle}>
        <RoleSelect
          name='role_type_id'
          value={state.role_type_id}
          onChange={handleRoleChange}
          isOrg={true}
          types={filterUserRoles(state.role_type_id)}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
          required
        />
      </div>

      <div
        css={css`
          height: 1px;
          background-color: ${colors.SOFT_STEEL};
          width: 100%;
        `}
      />

      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          Contact Info
        </Text>
      </div>

      <div
        css={css`
          display: flex;
          gap: 16px;
          ${isMobile &&
          css`
            flex-direction: column;
          `}
        `}
      >
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <TextInput
            name='email'
            label='Email'
            value={state.email}
            setValue={handleFieldChange('email')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            disabled={Boolean(hasExistingEmail)}
            placeholder='Email Address'
            type='email'
          />
        </div>
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <ModernPhoneInput
            value={state.phone_number}
            onChange={handlePhoneChange}
            onCountryChange={handlePhoneCountryChange}
            label='Phone Number'
            error={
              state.fieldErrors?.includes('phone_number')
                ? state.customError?.phone_number || 'Invalid phone number'
                : ''
            }
            validateOnBlur={false}
          />
        </div>
      </div>

      {!isEdit && state.email && (
        <Alert
          message={
            <Text
              variant='paragraphSmall'
              weight='regular'
              style={'width: 100%; margin-left: 8px;'}
            >
              Including An Email Address Will Send An Invitation To This User To
              Register With Sportninja.
            </Text>
          }
          type='warning'
          showIcon
          icon={
            <InfoCircleOutlined
              css={css`
                font-size: 20px;
                color: ${colors.WARNING_200};
              `}
            />
          }
          css={css`
            width: 100%;
            border-radius: 8px;
          `}
        />
      )}

      {isEdit && (
        <>
          <div
            css={css`
              height: 1px;
              background-color: ${colors.SOFT_STEEL};
              width: 100%;
              margin-top: 8px;
            `}
          />
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
              width: 100%;
            `}
          >
            <Text
              variant='captionSmall'
              weight='semiBold'
              color={colors.NEUTRAL_50}
            >
              Danger Zone
            </Text>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.WHITE}
            >
              Permanently Remove This User From The {entityLabel}.
            </Text>
            <DeleteButton onClick={handleDeleteClick} />
          </div>
        </>
      )}

      {state.error && (
        <Alert
          message={state.error}
          type='error'
          showIcon
          css={css`
            background: ${colors.ERROR_50};
            border: 1px solid ${colors.ERROR_300};
            border-radius: 8px;
            margin-top: 8px;
            .ant-alert-message {
              color: ${colors.ERROR_300};
            }
            .ant-alert-icon {
              color: ${colors.ERROR_300};
            }
          `}
        />
      )}

      <UserDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        userName={`${state.name_first} ${state.name_last}`}
        isLoading={isDeleting}
        error={deleteError}
        title='Remove User'
      />
    </div>
  )
}

export default UserModalContent
